import type {
    PermissionOperation,
    PermissionOperationHint,
    PermissionResourceCatalog,
    PermissionResourceKey,
    PermissionValue,
} from './types';

const operationFallback: Record<PermissionOperation, PermissionOperation[]> = {
    read: ['read', 'manage', 'write'],
    write: ['write', 'manage', 'read'],
    manage: ['manage', 'write', 'read'],
};

const genericFallbackOrder: PermissionOperation[] = ['read', 'write', 'manage'];

export function resolveResourcePermission<
    Catalog extends PermissionResourceCatalog<Value>,
    Value extends PermissionValue = PermissionValue
>(catalog: Catalog, resource: PermissionResourceKey<Catalog>, operation?: PermissionOperationHint): Value | undefined {
    const map = catalog[resource];
    if (!map) {
        console.warn(`[resolveResourcePermission] Unknown resource "${String(resource)}".`);
        return undefined;
    }

    if (operation) {
        for (const candidate of operationFallback[operation]) {
            const match = map[candidate];
            if (match) {
                return match;
            }
        }
    }

    for (const candidate of genericFallbackOrder) {
        const match = map[candidate];
        if (match) {
            return match;
        }
    }

    console.warn(`[resolveResourcePermission] No permission mapped for resource "${String(resource)}".`);
    return undefined;
}

export function inferOperationFromPath(path: string): PermissionOperation {
    const segments = path.split('/').filter(Boolean);
    if (!segments.length) {
        return 'read';
    }

    const writeTriggers = new Set(['new', 'form', 'edit', 'editar', 'create', 'participar']);
    if (segments.some((segment) => writeTriggers.has(segment))) {
        return 'write';
    }

    const paramIndex = segments.findIndex((segment) => segment.startsWith(':'));
    if (paramIndex >= 0) {
        const previous = paramIndex > 0 ? segments[paramIndex - 1] : '';
        if (!['details', 'detalhes', 'visualizar', 'view'].includes(previous)) {
            return 'write';
        }
    }

    return 'read';
}

export function resolveRoutePermission<
    Catalog extends PermissionResourceCatalog<Value>,
    Value extends PermissionValue = PermissionValue
>(
    catalog: Catalog,
    path: string,
    resource?: PermissionResourceKey<Catalog>,
    operationHint?: PermissionOperationHint
): Value | undefined {
    if (!resource) {
        return undefined;
    }

    const operation = operationHint ?? inferOperationFromPath(path);
    return resolveResourcePermission<Catalog, Value>(catalog, resource, operation);
}

export function createRoutePermissionResolver<
    Catalog extends PermissionResourceCatalog<Value>,
    Value extends PermissionValue = PermissionValue
>(catalog: Catalog) {
    return (path: string, resource?: PermissionResourceKey<Catalog>, operationHint?: PermissionOperationHint) =>
        resolveRoutePermission<Catalog, Value>(catalog, path, resource, operationHint);
}

export interface AllowResourceOptions {
    operations?: string | string[];
}

export function allowResourceFromCatalog<
    Catalog extends Record<string, Value>,
    Value extends PermissionValue = PermissionValue
>(catalog: Catalog, resource: string, options?: AllowResourceOptions): Value[] {
    const values = Object.values(catalog) as Value[];
    const prefix = `${resource}:`;
    const operations = options?.operations;
    const operationSet = operations
        ? new Set(Array.isArray(operations) ? operations : [operations])
        : undefined;

    const matches = values.filter((permission) => {
        if (typeof permission !== 'string' || !permission.startsWith(prefix)) {
            return false;
        }
        if (!operationSet) {
            return true;
        }
        const operation = permission.slice(prefix.length);
        return operationSet.has(operation);
    });

    if (!matches.length) {
        console.warn(`[allowResource] No permissions matched prefix "${prefix}".`);
    }

    return matches;
}
